import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import {
  buildChangeOrderAttachmentInsertPayload,
  coVisibilityLabel,
  isChangeOrderFileAllowed,
  normalizeCoAttachmentVisibility,
  type CoAttachmentVisibility,
} from '@/lib/changeOrderUtils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UploadFileItem {
  id: string;
  file: File;
  previewUrl: string | null;
  caption: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  progress: number;
  errorMsg: string;
  uploadedRecord: UploadedFileRecord | null;
}

export interface UploadedFileRecord {
  id: string;
  file_name: string;
  file_url: string;
  storage_bucket?: string | null;
  storage_path?: string | null;
  file_type: string | null;
  file_size: string | number | null;
  note: string | null;
  photo_type?: string | null;
  is_client_visible?: boolean | null;
  is_subcontractor_visible?: boolean | null;
  change_order_id?: string | null;
  created_at?: string | null;
}

export interface FileUploaderProps {
  projectId: string;
  currentUserId: string;
  open: boolean;
  onClose: () => void;
  onUploadComplete: (records: UploadedFileRecord[]) => void;

  // Behaviour config
  accountType: 'management' | 'subcontractor' | 'client';
  category?: 'photo' | 'file' | 'mixed' | 'change_order';
  photoType?: string | null;
  allowMultiple?: boolean;
  allowCamera?: boolean;
  allowPdf?: boolean;
  requireCaption?: boolean;
  enablePerFileCaption?: boolean;
  visibilityScope?: 'client' | 'subcontractor' | 'management' | 'all';
  /** When true, show visibility selector (Change Order attachments). */
  showVisibilitySelector?: boolean;
  relatedType?: 'question' | 'instruction' | 'progress_update' | 'change_order' | 'daily_log' | null;
  relatedId?: string | null;

  // Storage config
  bucket?: 'project-files' | 'project-photos' | 'daily-log-photos' | 'instruction-attachments' | 'change-order-photos';
  storagePath?: string;

  // UI config
  title?: string;
  folderId?: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MB = 1024 * 1024;
const MAX_FILE_SIZE = 20 * MB;
const MAX_TOTAL_SIZE = 100 * MB;
const MAX_FILES = 20;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < MB) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / MB).toFixed(1)} MB`;
}

function getFileExt(name: string): string {
  return name.split('.').pop()?.toLowerCase() || 'other';
}

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

function generateId(): string {
  return `uf-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function isChangeOrderAttachmentUpload(
  relatedType: FileUploaderProps['relatedType'],
  photoType: FileUploaderProps['photoType'],
  category?: FileUploaderProps['category']
): boolean {
  return (relatedType === 'change_order' || category === 'change_order') && photoType !== 'co_invoice';
}

/** Management Change Order uploads default to Internal only — never "both". */
function resolveInitialAttachmentVisibility(
  relatedType: FileUploaderProps['relatedType'],
  photoType: FileUploaderProps['photoType'],
  visibilityScope: FileUploaderProps['visibilityScope'],
  category?: FileUploaderProps['category']
): CoAttachmentVisibility {
  if (isChangeOrderAttachmentUpload(relatedType, photoType, category)) {
    if (
      visibilityScope === 'client' ||
      visibilityScope === 'subcontractor' ||
      visibilityScope === 'management'
    ) {
      return visibilityScope;
    }
    return 'management';
  }
  return visibilityScope === 'client'
    ? 'client'
    : visibilityScope === 'subcontractor'
      ? 'subcontractor'
      : visibilityScope === 'management'
        ? 'management'
        : 'management';
}

/** Determine bucket from context. Rules per Part 8: */
function resolveBucket(props: {
  bucket?: FileUploaderProps['bucket'];
  relatedType?: FileUploaderProps['relatedType'];
  category?: string;
}): string {
  if (props.bucket) return props.bucket;
  if (props.relatedType === 'change_order') return 'change-order-photos';
  if (props.relatedType === 'daily_log') return 'daily-log-photos';
  if (props.relatedType === 'instruction') return 'instruction-attachments';
  if (props.category === 'photo') return 'project-photos';
  return 'project-files';
}

/** Generate storage path per Part 8 requirements */
function resolveStoragePath(props: {
  bucket: string;
  projectId: string;
  relatedId?: string | null;
  logDate?: string | null;
}): string {
  const timestamp = Date.now();
  const { bucket, projectId, relatedId, logDate } = props;

  switch (bucket) {
    case 'project-photos':
      return `project-photos/${projectId}/${timestamp}-`;
    case 'daily-log-photos':
      return `daily-log-photos/${projectId}/${relatedId || logDate || 'general'}/${timestamp}-`;
    case 'instruction-attachments':
      return `instruction-attachments/${projectId}/${relatedId || 'general'}/${timestamp}-`;
    case 'change-order-photos':
      return `change-order-photos/${projectId}/${relatedId || 'general'}/${timestamp}-`;
    case 'project-files':
    default:
      return `project-files/${projectId}/${timestamp}-`;
  }
}

function acceptedFileTypes(category: string, allowPdf: boolean): string {
  if (category === 'change_order') {
    return '.jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf';
  }
  if (category === 'photo') return 'image/*,video/*';
  if (category === 'file') return '.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.jpg,.jpeg,.png,.gif,.webp';
  return allowPdf
    ? 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip'
    : 'image/*,.doc,.docx,.xls,.xlsx,.txt,.zip';
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function FileUploader({
  projectId,
  currentUserId,
  open,
  onClose,
  onUploadComplete,
  accountType,
  category = 'mixed',
  photoType,
  allowMultiple = true,
  allowCamera = true,
  allowPdf = true,
  requireCaption = false,
  enablePerFileCaption = true,
  visibilityScope = 'all',
  showVisibilitySelector = false,
  relatedType,
  relatedId,
  bucket: bucketProp,
  storagePath: storagePathProp,
  title = 'Upload',
  folderId,
}: FileUploaderProps) {
  const [items, setItems] = useState<UploadFileItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const isCoAttachmentUpload = isChangeOrderAttachmentUpload(relatedType, photoType, category);
  const effectiveShowVisibilitySelector = showVisibilitySelector || isCoAttachmentUpload;
  const [attachmentVisibility, setAttachmentVisibility] = useState<CoAttachmentVisibility>(
    resolveInitialAttachmentVisibility(relatedType, photoType, visibilityScope, category)
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const resolvedBucket = resolveBucket({ bucket: bucketProp, relatedType, category });
  const basePath = storagePathProp || resolveStoragePath({
    bucket: resolvedBucket,
    projectId,
    relatedId,
  });

  const acceptAttr = acceptedFileTypes(category, allowPdf);

  // Reset when opening
  useEffect(() => {
    if (open) {
      setItems([]);
      setGlobalError('');
      setIsUploading(false);
      setAttachmentVisibility(
        resolveInitialAttachmentVisibility(relatedType, photoType, visibilityScope, category)
      );
    }
  }, [open, visibilityScope, relatedType, photoType, category]);

  // Cleanup object URLs
  useEffect(() => {
    return () => {
      items.forEach((it) => {
        if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
      });
    };
  }, []);

  const addFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      const newFiles: UploadFileItem[] = [];
      let totalSize = items.reduce((s, it) => s + it.file.size, 0);

      for (const file of Array.from(files)) {
        if (items.length + newFiles.length >= MAX_FILES) {
          setGlobalError(t('upload.maxFiles', { max: MAX_FILES }));
          break;
        }
        if (file.size > MAX_FILE_SIZE) {
          setGlobalError(`${file.name} ${t('upload.fileTooLarge', { max: formatBytes(MAX_FILE_SIZE) })}`);
          continue;
        }
        if (totalSize + file.size > MAX_TOTAL_SIZE) {
          setGlobalError(t('upload.totalExceeds', { max: formatBytes(MAX_TOTAL_SIZE) }));
          break;
        }

        const ext = getFileExt(file.name);
        const isImg = isImageFile(file);

        // Validate type
        if (category === 'photo' && !isImg) {
          setGlobalError(`${file.name} ${t('upload.notAnImage')}`);
          continue;
        }
        if (category === 'change_order' && !isChangeOrderFileAllowed(file)) {
          setGlobalError(`${file.name}: Only JPG, JPEG, PNG, WebP, and PDF files are allowed.`);
          continue;
        }
        if (!allowPdf && ext === 'pdf') {
          setGlobalError(`${file.name}: ${t('upload.pdfNotAllowed')}`);
          continue;
        }

        const previewUrl = isImg ? URL.createObjectURL(file) : null;
        newFiles.push({
          id: generateId(),
          file,
          previewUrl,
          caption: '',
          status: 'pending',
          progress: 0,
          errorMsg: '',
          uploadedRecord: null,
        });

        totalSize += file.size;
      }

      setItems((prev) => [...prev, ...newFiles]);
      setGlobalError('');
    },
    [items, category, allowPdf, t]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const removeItem = (id: string) => {
    setItems((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const updateCaption = (id: string, caption: string) => {
    setItems((prev) => prev.map((p) => (p.id === id ? { ...p, caption } : p)));
  };

  async function handleUpload() {
    if (items.length === 0 || !currentUserId) return;

    // Validate captions if required
    if (requireCaption && enablePerFileCaption) {
      const missing = items.filter((it) => !it.caption.trim() && it.status === 'pending');
      if (missing.length > 0) {
        setGlobalError(
          missing.length > 1
            ? t('upload.addCaptionForPlural', { count: missing.length })
            : t('upload.addCaptionFor', { count: missing.length })
        );
        return;
      }
    }

    setIsUploading(true);
    setGlobalError('');
    const uploadedRecords: UploadedFileRecord[] = [];
    const normalizedVisibility = normalizeCoAttachmentVisibility(attachmentVisibility);

    try {
      const pendingItems = items.filter((it) => it.status !== 'done' && it.status !== 'error');

      for (const item of pendingItems) {
        setItems((prev) =>
          prev.map((p) => (p.id === item.id ? { ...p, status: 'uploading', progress: 10 } : p))
        );

        let publicUrl = '';
        let dbRecordId = '';

        try {
          const ext = getFileExt(item.file.name);
          const safeName = item.file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
          const path = `${basePath}${Date.now()}_${item.id}_${safeName}`;

          const { error: storageError } = await supabase.storage
            .from(resolvedBucket)
            .upload(path, item.file, { upsert: false });

          if (storageError) {
            throw new Error(`Storage upload failed: ${storageError.message}`);
          }

          setItems((prev) =>
            prev.map((p) => (p.id === item.id ? { ...p, progress: 50 } : p))
          );

          const { data: urlData } = supabase.storage.from(resolvedBucket).getPublicUrl(path);
          publicUrl = urlData.publicUrl;

          if (!publicUrl) {
            throw new Error('Failed to get public URL from storage');
          }

          setItems((prev) =>
            prev.map((p) => (p.id === item.id ? { ...p, progress: 70 } : p))
          );

          const dbPayload: Record<string, unknown> =
            relatedType === 'change_order' && photoType === 'co_invoice'
              ? {
                  project_id: projectId,
                  folder_id: folderId || null,
                  file_name: item.file.name,
                  file_url: publicUrl,
                  storage_bucket: resolvedBucket,
                  storage_path: path,
                  file_size: String(item.file.size),
                  file_type: ext,
                  note: item.caption.trim() || null,
                  uploaded_by: currentUserId,
                  is_visible: true,
                  is_client_visible: false,
                  is_deleted: false,
                  photo_type: photoType,
                  uploaded_context: 'change_order',
                  ...(relatedId ? { change_order_id: relatedId } : {}),
                }
              : relatedType === 'change_order'
              ? {
                  ...buildChangeOrderAttachmentInsertPayload({
                    projectId,
                    folderId,
                    fileName: item.file.name,
                    fileUrl: publicUrl,
                    fileSize: item.file.size,
                    fileType: ext,
                    note: item.caption.trim() || null,
                    uploadedBy: currentUserId,
                    visibility: normalizedVisibility,
                    changeOrderId: relatedId,
                  }),
                  // Phase 1 signed-URL migration: persist storage reference
                  // alongside the legacy public file_url.
                  storage_bucket: resolvedBucket,
                  storage_path: path,
                }
              : {
                  project_id: projectId,
                  folder_id: folderId || null,
                  file_name: item.file.name,
                  file_url: publicUrl,
                  // Phase 1 signed-URL migration: persist storage reference
                  // alongside the legacy public file_url.
                  storage_bucket: resolvedBucket,
                  storage_path: path,
                  file_size: String(item.file.size),
                  file_type: ext,
                  note: item.caption.trim() || null,
                  uploaded_by: currentUserId,
                  is_visible: true,
                  is_client_visible:
                    normalizedVisibility === 'client' || normalizedVisibility === 'all',
                  is_subcontractor_visible:
                    normalizedVisibility === 'subcontractor' || normalizedVisibility === 'all',
                  is_deleted: false,
                  ...(photoType ? { photo_type: photoType } : {}),
                  ...(relatedType === 'instruction' && relatedId ? { instruction_id: relatedId } : {}),
                };

          const { data: inserted, error: dbError } = await supabase
            .from('jphph_project_files')
            .insert(dbPayload)
            .select('id, file_name, file_url, storage_bucket, storage_path, file_type, file_size, note, photo_type, is_client_visible, is_subcontractor_visible, change_order_id, created_at')
            .maybeSingle();

          if (dbError) {
            console.error('[FileUploader] change order attachment insert failed', {
              table: 'jphph_project_files',
              visibility: normalizedVisibility,
              photo_type: (dbPayload.photo_type as string | undefined) ?? null,
              message: dbError.message,
              code: dbError.code,
            });
            throw new Error(`Database insert failed: ${dbError.message}`);
          }

          if (!inserted?.id) {
            throw new Error('Database insert returned no attachment record');
          }

          dbRecordId = inserted.id;

          const record: UploadedFileRecord = {
            id: dbRecordId,
            file_name: inserted.file_name ?? item.file.name,
            file_url: inserted.file_url ?? publicUrl,
            storage_bucket: inserted.storage_bucket ?? resolvedBucket,
            storage_path: inserted.storage_path ?? path,
            file_type: inserted.file_type ?? ext,
            file_size: inserted.file_size ?? item.file.size,
            note: (inserted.note ?? item.caption.trim()) || null,
            photo_type: inserted.photo_type ?? (dbPayload.photo_type as string | null | undefined) ?? null,
            is_client_visible: inserted.is_client_visible ?? (dbPayload.is_client_visible as boolean | null | undefined) ?? null,
            is_subcontractor_visible: inserted.is_subcontractor_visible ?? (dbPayload.is_subcontractor_visible as boolean | null | undefined) ?? null,
            change_order_id: inserted.change_order_id ?? (dbPayload.change_order_id as string | null | undefined) ?? null,
            created_at: inserted.created_at ?? null,
          };

          uploadedRecords.push(record);

          setItems((prev) =>
            prev.map((p) =>
              p.id === item.id ? { ...p, status: 'done', progress: 100, uploadedRecord: record } : p
            )
          );
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Upload failed';

          if (publicUrl && !dbRecordId) {
            console.error(
              `[FileUploader] DB insert failed but file uploaded to ${resolvedBucket}. URL: ${publicUrl}. Error: ${msg}`
            );
          }

          setGlobalError(msg);
          setItems((prev) =>
            prev.map((p) => (p.id === item.id ? { ...p, status: 'error', errorMsg: msg } : p))
          );
        }
      }

      if (uploadedRecords.length > 0) {
        onUploadComplete(uploadedRecords);
      }
    } finally {
      setIsUploading(false);
    }
  }

  const handleClose = () => {
    if (isUploading) return;
    items.forEach((it) => {
      if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
    });
    onClose();
  };

  if (!open) return null;

  const hasPending = items.some((it) => it.status === 'pending');
  const hasErrors = items.some((it) => it.status === 'error');
  const allDone = items.length > 0 && items.every((it) => it.status === 'done');
  const doneCount = items.filter((it) => it.status === 'done').length;

  // Camera-first UI helpers
  const isPhotoCategory = category === 'photo';
  const isChangeOrderCategory = category === 'change_order';
  const showCameraButton = allowCamera && (isPhotoCategory || isChangeOrderCategory);
  const showGalleryButton = isPhotoCategory || isChangeOrderCategory;
  const showFileButton = category === 'file' || category === 'mixed' || isChangeOrderCategory;

  return (
    <div className="fixed inset-0 z-[60] flex items-end lg:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => {
          if (!isUploading) handleClose();
        }}
      />

      {/* Modal */}
      <div className="relative bg-white w-full lg:max-w-lg rounded-t-3xl lg:rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-[#1A1A1A] font-bold text-base">{title}</h3>
            {items.length > 0 && (
              <p className="text-[#9CA3AF] text-xs mt-0.5">
                {doneCount}/{items.length} {t('upload.uploaded')}
              </p>
            )}
          </div>
          {!isUploading && (
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer"
            >
              <i className="ri-close-line text-[#374151]"></i>
            </button>
          )}
        </div>

        <div className="px-5 py-4 space-y-4">
          {effectiveShowVisibilitySelector && (
            <fieldset className="space-y-2">
              <legend className="text-[#374151] text-xs font-semibold">Attachment visibility</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(['management', 'client', 'subcontractor', 'all'] as CoAttachmentVisibility[]).map((scope) => (
                  <label
                    key={scope}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-left text-xs font-medium cursor-pointer transition-colors ${
                      attachmentVisibility === scope
                        ? 'border-[#E8621A] bg-[#FEF0E7]/40 text-[#E8621A]'
                        : 'border-gray-200 text-[#374151] hover:border-[#E8621A]/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="change-order-attachment-visibility"
                      value={scope}
                      checked={attachmentVisibility === scope}
                      onChange={() => setAttachmentVisibility(scope)}
                      className="accent-[#E8621A] cursor-pointer"
                    />
                    <span>{coVisibilityLabel(scope)}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {/* ── Camera-first action buttons (mobile/tablet friendly) ── */}
          <div className="space-y-3">
            {/* Hidden file inputs */}
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptAttr}
              multiple={allowMultiple}
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />

            {/* Desktop: drag & drop zone */}
            <div
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`${isChangeOrderCategory ? 'block' : 'hidden md:block'} border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                isDragging
                  ? 'border-[#E8621A] bg-[#FEF0E7]/40'
                  : 'border-[#E0DDD8] hover:border-[#E8621A] bg-white'
              }`}
            >
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 text-[#9CA3AF]">
                <i className="ri-upload-cloud-2-line text-3xl"></i>
              </div>
              <p className="text-[#374151] text-sm font-medium">
                {t('upload.dropFilesHere')}
              </p>
              <p className="text-[#9CA3AF] text-xs mt-0.5">
                {category === 'change_order'
                  ? 'JPG, JPEG, PNG, WebP, PDF'
                  : category === 'photo'
                  ? t('upload.jpgPngWebpHeic')
                  : allowPdf
                    ? t('upload.jpgPngPdfDocs')
                    : t('upload.jpgPngDocs')}
                {' · '}
                {t('upload.maxEach', { max: formatBytes(MAX_FILE_SIZE) })}
              </p>
            </div>

            {/* Mobile: 3 large tap targets */}
            <div className="md:hidden grid grid-cols-3 gap-2">
              {showCameraButton && (
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex flex-col items-center gap-1.5 py-4 rounded-xl border-2 border-gray-200 hover:border-[#E8621A] hover:bg-[#FEF0E7]/20 cursor-pointer transition-colors active:scale-95"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#FEF0E7]">
                    <i className="ri-camera-line text-[#E8621A] text-xl"></i>
                  </div>
                  <span className="text-[#374151] text-[11px] font-semibold">{t('upload.takePhoto')}</span>
                </button>
              )}
              {showGalleryButton && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center gap-1.5 py-4 rounded-xl border-2 border-gray-200 hover:border-[#E8621A] hover:bg-[#FEF0E7]/20 cursor-pointer transition-colors active:scale-95"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#FEF0E7]">
                    <i className="ri-image-line text-[#E8621A] text-xl"></i>
                  </div>
                  <span className="text-[#374151] text-[11px] font-semibold">{t('upload.choosePhotos')}</span>
                </button>
              )}
              {showFileButton && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center gap-1.5 py-4 rounded-xl border-2 border-gray-200 hover:border-[#E8621A] hover:bg-[#FEF0E7]/20 cursor-pointer transition-colors active:scale-95"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#FEF0E7]">
                    <i className="ri-file-upload-line text-[#E8621A] text-xl"></i>
                  </div>
                  <span className="text-[#374151] text-[11px] font-semibold">{t('upload.uploadFile')}</span>
                </button>
              )}
            </div>

            {/* Desktop fallback single button */}
            <div className="hidden md:block">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-3 rounded-xl border-2 border-dashed border-gray-200 text-[#9CA3AF] text-sm font-medium hover:border-[#E8621A] hover:text-[#E8621A] hover:bg-[#FEF0E7]/20 cursor-pointer transition-colors flex items-center justify-center gap-2"
              >
                <i className="ri-add-line"></i>
                {category === 'photo' ? t('upload.selectPhotos') : t('upload.selectFiles')}
              </button>
            </div>
          </div>

          {/* ── File preview list ── */}
          {items.length > 0 && (
            <div className="space-y-3">
              <p className="text-[#374151] text-xs font-semibold">
                {items.length} {items.length > 1 ? t('upload.files') : t('upload.file')} {t('upload.selected')}
              </p>

              <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-1">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-start gap-3 rounded-xl p-3 border transition-colors ${
                      item.status === 'error'
                        ? 'bg-[#FEF2F2] border-[#FECACA]'
                        : item.status === 'done'
                          ? 'bg-[#F0FDF4] border-[#BBF7D0]'
                          : 'bg-[#F9FAFB] border-gray-100'
                    }`}
                  >
                    {/* Thumbnail */}
                    {item.previewUrl ? (
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                        <img
                          src={item.previewUrl}
                          alt={item.file.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-[#FEF0E7] flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-line text-[#E8621A] text-2xl"></i>
                      </div>
                    )}

                    {/* Info + caption */}
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-[#374151] text-sm font-medium truncate">
                            {item.file.name}
                          </p>
                          <p className="text-[#9CA3AF] text-[11px]">
                            {formatBytes(item.file.size)}
                          </p>
                        </div>
                        {/* Status icon */}
                        {item.status === 'done' && (
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <i className="ri-checkbox-circle-fill text-[#059669] text-lg"></i>
                          </div>
                        )}
                        {item.status === 'error' && (
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <i className="ri-error-warning-fill text-[#EF4444] text-lg"></i>
                          </div>
                        )}
                        {item.status === 'uploading' && (
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <i className="ri-loader-4-line animate-spin text-[#E8621A] text-base"></i>
                          </div>
                        )}
                      </div>

                      {/* Per-file caption */}
                      {enablePerFileCaption && (
                        <input
                          type="text"
                          value={item.caption}
                          onChange={(e) => updateCaption(item.id, e.target.value)}
                          placeholder={t('upload.addCaption')}
                          maxLength={200}
                          disabled={item.status === 'uploading' || item.status === 'done'}
                          className="w-full border border-[#E0DDD8] rounded-lg px-3 py-2 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none focus:border-[#E8621A] transition-colors disabled:opacity-50"
                        />
                      )}

                      {/* Error message */}
                      {item.status === 'error' && item.errorMsg && (
                        <p className="text-[#EF4444] text-xs">{item.errorMsg}</p>
                      )}

                      {/* Progress bar */}
                      {item.status === 'uploading' && (
                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#E8621A] rounded-full transition-all"
                            style={{ width: `${item.progress}%` }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Remove button */}
                    {item.status !== 'uploading' && item.status !== 'done' && (
                      <button
                        onClick={() => removeItem(item.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-[#9CA3AF] hover:text-[#EF4444] hover:bg-[#FEF2F2] cursor-pointer transition-colors flex-shrink-0"
                      >
                        <i className="ri-close-line text-base"></i>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Global error ── */}
          {globalError && (
            <div className="flex items-start gap-2 bg-[#FEF2F2] rounded-xl px-3 py-2.5">
              <i className="ri-error-warning-line text-[#EF4444] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-[#EF4444] text-xs">{globalError}</p>
            </div>
          )}

          {/* ── Upload / action button ── */}
          {items.length > 0 && (
            <button
              onClick={handleUpload}
              disabled={isUploading || !hasPending}
              className="w-full py-3.5 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
            >
              {isUploading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  {t('upload.uploading')}
                </>
              ) : hasErrors && !hasPending ? (
                <>
                  <i className="ri-restart-line"></i>
                  {t('upload.retryFailed')}
                </>
              ) : allDone ? (
                <>
                  <i className="ri-checkbox-circle-fill"></i>
                  {t('upload.done')} — {doneCount} {t('upload.uploaded')}
                </>
              ) : (
                <>
                  <i className="ri-upload-2-line"></i>
                  {t('upload.upload')} {items.filter((it) => it.status === 'pending').length} {items.filter((it) => it.status === 'pending').length > 1 ? t('upload.files') : t('upload.file')}
                </>
              )}
            </button>
          )}

          {/* ── Keep open hint ── */}
          {allDone && (
            <p className="text-[#9CA3AF] text-xs text-center">
              {t('upload.closingMoment')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
